import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import type { LucideIcon } from "lucide-react-native";
import {
  Syringe,
  TestTube2,
  Activity,
  Clock3,
  Snowflake,
  CircleOff,
  CircleDotDashed,
  FileQuestion,
  PackageOpen,
  Pill,
} from "lucide-react-native";
import {
  QuestionLayout,
  RectangularOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useFertilityTreatments } from "../FertilityTreatmentsContext";
import { buildTreatmentNavigationStack } from "../getNextTreatmentScreen";
import type { FertilityTreatmentsStackParamList } from "../FertilityTreatmentsNavigator";

type TreatmentType =
  | "ivf_icsi"
  | "iui"
  | "oi"
  | "meds_only"
  | "timed_intercourse"
  | "other"
  | "unknown"
  | "medicated_cycle"
  | "fet"
  | "egg_freezing"
  | "embryo_freezing";

type TreatmentSex = "AFAB" | "AMAB" | "both";

const treatmentTypes: {
  id: TreatmentType;
  labelKey: string;
  icon: LucideIcon;
  sex: TreatmentSex;
}[] = [
  {
    id: "ivf_icsi",
    labelKey: "med_history.fertility.treatments.type.ivf_icsi",
    icon: Syringe,
    sex: "both",
  },
  {
    id: "iui",
    labelKey: "med_history.fertility.treatments.type.iui",
    icon: TestTube2,
    sex: "both",
  },
  // Ovarian induction is AFAB-anatomy specific
  {
    id: "oi",
    labelKey: "med_history.fertility.treatments.type.ovulation_induction",
    icon: Activity,
    sex: "AFAB",
  },
  {
    id: "meds_only",
    labelKey: "med_history.fertility.treatments.type.medications_only",
    icon: Pill,
    sex: "both",
  },
  {
    id: "timed_intercourse",
    labelKey: "med_history.fertility.treatments.type.timed_intercourse",
    icon: Clock3,
    sex: "both",
  },
  {
    id: "other",
    labelKey: "med_history.fertility.treatments.type.other_treatment",
    icon: PackageOpen,
    sex: "both",
  },
  {
    id: "unknown",
    labelKey: "med_history.fertility.treatments.type.unknown_treatment",
    icon: FileQuestion,
    sex: "both",
  },
  {
    id: "fet",
    labelKey: "med_history.fertility.treatments.type.fet",
    icon: Snowflake,
    sex: "both",
  },
  {
    id: "egg_freezing",
    labelKey: "med_history.fertility.treatments.type.egg_freezing",
    icon: CircleDotDashed,
    sex: "AFAB",
  },
  {
    id: "embryo_freezing",
    labelKey: "med_history.fertility.treatments.type.embryo_freezing",
    icon: PackageOpen,
    sex: "both",
  },
];

export function FertilityTreatmentsTypesScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTreatments();
  const navigation =
    useNavigation<NavigationProp<FertilityTreatmentsStackParamList>>();
  const { data: patient } = usePatient();
  const patientSex = patient?.gender;

  const visibleTypes = treatmentTypes.filter(
    (treatment) =>
      treatment.sex === "both" || !patientSex || treatment.sex === patientSex,
  );

  const selected = new Set(
    (state.selectedTypes || []).map((type) =>
      type === "medicated_cycle" ? "oi" : type,
    ),
  );

  const toggleSelection = (type: TreatmentType) => {
    const newSelection = new Set(selected);
    if (newSelection.has(type)) {
      newSelection.delete(type);

      // Remove treatments whose type is deselected; collect their IDs for deletion
      const remainingTreatments: typeof state.treatments = [];
      const idsToDelete: string[] = [...state.treatmentIdsToDelete];

      for (const tr of state.treatments) {
        const treatmentType = (tr.type || "") as string;
        if (
          treatmentType === type ||
          (type === "oi" && treatmentType === "medicated_cycle")
        ) {
          if (tr.id) idsToDelete.push(tr.id);
          continue; // drop from state
        }
        remainingTreatments.push(tr);
      }

      updateState({
        hasTreatmentsDone: newSelection.size > 0 ? true : null,
        selectedTypes: Array.from(newSelection),
        treatments: remainingTreatments,
        treatmentIdsToDelete: idsToDelete,
      });
    } else {
      newSelection.add(type);
      updateState({
        hasTreatmentsDone: true,
        selectedTypes: Array.from(newSelection),
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.treatments
      .filter((treatment) => treatment.id)
      .map((treatment) => treatment.id!);

    updateState({
      hasTreatmentsDone: false,
      selectedTypes: [],
      treatments: [],
      treatmentIdsToDelete: Array.from(
        new Set([...state.treatmentIdsToDelete, ...idsToDelete]),
      ),
    });
  };

  const handleSave = () => {
    const stack = buildTreatmentNavigationStack(Array.from(selected));
    updateState({
      hasTreatmentsDone: state.hasTreatmentsDone === false ? false : true,
      navigationStack: stack,
      currentNavigationIndex: 0,
    });

    navigation.navigate(stack[0] as keyof FertilityTreatmentsStackParamList);
  };

  const noneSelected = state.hasTreatmentsDone === false;

  return (
    <QuestionLayout
      title={t("med_history.fertility.treatments.types_title")}
      subtitle={t("med_history.fertility.treatments.types_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selected.size === 0 && !noneSelected}
      saveLabel={t("med_history.fertility.continue")}
    >
      <View style={styles.optionsContainer}>
        {visibleTypes.map((treatment) => (
          <RectangularOptionButton
            key={treatment.id}
            icon={treatment.icon}
            label={t(treatment.labelKey)}
            isSelected={selected.has(treatment.id)}
            onPress={() => toggleSelection(treatment.id)}
            iconSize={20}
          />
        ))}

        <RectangularOptionButton
          icon={CircleOff}
          label={t("med_history.fertility.treatments.none")}
          isSelected={noneSelected}
          onPress={handleNone}
          iconSize={20}
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    width: "100%",
    gap: 12,
    marginBottom: 40,
  },
});

export default FertilityTreatmentsTypesScreen;
